import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Paper } from '@mui/material';
import { colors, spacing, borderRadius, shadows } from '../constants';
import { useRaiseQueryPageState } from '../hooks/queries/raiseQuery/useRaiseQueryPageState';
import { useRaiseQueryPageData } from '../hooks/queries/raiseQuery/useRaiseQueryPageData';
import { useRaiseQueryPageFilters } from '../hooks/queries/raiseQuery/useRaiseQueryPageFilters';
import { useRaiseQueryPageOperations } from '../hooks/queries/raiseQuery/useRaiseQueryPageOperations';
import { useRaiseQueryPageModals } from '../hooks/queries/raiseQuery/useRaiseQueryPageModals';
import ViewQueryModal from '../components/queries/ViewQueryModal';
import EditQueryModal from '../components/queries/EditQueryModal';
import ConfirmationDialog from '../components/common/ConfirmationDialog';
import SearchAndFilter from '../components/queries/SearchAndFilter';
import EscalateQueryDialog from '../components/queries/EscalateQueryDialog';
import RaiseNewQueryButton from '../components/raiseQueryPage/RaiseNewQueryButton';
import RegionCardsGrid from '../components/raiseQueryPage/RegionCardsGrid';
import QueryTable from '../components/raiseQueryPage/QueryTable';

const RaiseQuery = () => {
  const navigate = useNavigate();
  const tableContainerRef = useRef(null);

  // Initialize all hooks
  const pageState = useRaiseQueryPageState();
  const {
    fetchQueries,
    projectsCatalog,
  } = useRaiseQueryPageData({
    searchQuery: pageState.searchQuery,
    filters: pageState.filters,
    currentUserRole: pageState.currentUserRole,
    currentUserRegion: pageState.currentUserRegion,
    setCurrentUserRole: pageState.setCurrentUserRole,
    setCurrentUserRegion: pageState.setCurrentUserRegion,
    setAllQueries: pageState.setAllQueries,
    setLoading: pageState.setLoading,
    setProjectOptions: pageState.setProjectOptions,
    setAssignedFilterOptions: pageState.setAssignedFilterOptions,
    setQueryTypeOptions: pageState.setQueryTypeOptions,
    setStatusSummary: pageState.setStatusSummary,
    setBackendCounts: pageState.setBackendCounts,
    setEdUsersByRegion: pageState.setEdUsersByRegion,
  });

  const {
    filteredQueries,
    paginatedQueries,
    groupedQueriesByProject,
    paginatedProjects,
    totalProjects,
  } = useRaiseQueryPageFilters({
    allQueries: pageState.allQueries,
    selectedRegionCard: pageState.selectedRegionCard,
    selectedStatusCard: pageState.selectedStatusCard,
    tableAssignedFilter: pageState.tableAssignedFilter,
    page: pageState.page,
    rowsPerPage: pageState.rowsPerPage,
    visibleRegionOptions: pageState.visibleRegionOptions,
    backendCounts: pageState.backendCounts,
    setQueries: pageState.setQueries,
    setTotal: pageState.setTotal,
    setStatusSummary: pageState.setStatusSummary,
    setRegionStatusSummary: pageState.setRegionStatusSummary,
    setPage: pageState.setPage,
  });

  const operations = useRaiseQueryPageOperations(fetchQueries);
  const modals = useRaiseQueryPageModals();

  // Handlers
  const handleRegionCardClick = (regionCode) => {
    if (pageState.selectedRegionCard === regionCode) {
      pageState.setSelectedRegionCard(null);
      pageState.setSelectedStatusCard(null);
    } else {
      pageState.setSelectedRegionCard(regionCode);
      pageState.setSelectedStatusCard(null);
      // Scroll to table after a short delay to ensure state update
      setTimeout(() => {
        if (tableContainerRef.current) {
          tableContainerRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
    pageState.setPage(0);
  };

  const handleStatusCardClick = (statusLabel, regionCode, ref) => {
    const isStatusSelected = pageState.selectedRegionCard === regionCode && pageState.selectedStatusCard === statusLabel;
    if (isStatusSelected) {
      pageState.setSelectedStatusCard(null);
    } else {
      pageState.setSelectedRegionCard(regionCode);
      pageState.setSelectedStatusCard(statusLabel);
      // Scroll to table after a short delay to ensure state update
      setTimeout(() => {
        if (ref?.current) {
          ref.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
    pageState.setPage(0);
  };

  const handleTableAssignedFilterChange = (e) => {
    pageState.setTableAssignedFilter(e.target.value);
    pageState.setPage(0);
  };

  const handleView = (query) => {
    modals.handleOpenViewModal(query);
  };

  const handleEdit = (query) => {
    modals.handleOpenEditModal(query);
  };

  const handleDelete = (query) => {
    modals.handleOpenDeleteDialog(query);
  };

  const handleClose = (query) => {
    const result = operations.handleClose(query);
    if (result) {
      modals.handleOpenCloseDialog(query);
    }
  };

  const handleEscalateClick = (query) => {
    const result = operations.handleEscalateClick(query);
    if (result) {
      modals.handleOpenEscalateDialog(query);
    }
  };

  const handleConfirmEscalation = async (escalationData) => {
    if (!modals.escalateTarget) return;
    await operations.handleConfirmEscalation(modals.escalateTarget, escalationData);
    modals.handleCloseEscalateDialog();
  };

  const handleConfirmDelete = async () => {
    if (!modals.selectedQuery) return;
    await operations.confirmDelete(modals.selectedQuery);
    modals.handleCloseDeleteDialog();
  };

  const handleConfirmClose = async () => {
    if (!modals.selectedQuery) return;
    await operations.handleConfirmClose(modals.selectedQuery);
    modals.handleCloseCloseDialog();
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: colors.background, p: { xs: spacing.sm, sm: spacing.md } }}>
      <Box sx={{ width: '100%', maxWidth: 'none' }}>
        {/* Raise New Query Button at Top */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: spacing.md }}>
          <RaiseNewQueryButton
            onClick={() => navigate('/dashboard/queries/raise/new')}
            fullWidth={typeof window !== 'undefined' && window.innerWidth < 600}
          />
        </Box>

        {/* Region Cards Grid */}
        <RegionCardsGrid
          visibleRegionOptions={pageState.visibleRegionOptions}
          regionStatusSummary={pageState.regionStatusSummary}
          edUsersByRegion={pageState.edUsersByRegion}
          selectedRegionCard={pageState.selectedRegionCard}
          selectedStatusCard={pageState.selectedStatusCard}
          onRegionCardClick={handleRegionCardClick}
          onStatusCardClick={handleStatusCardClick}
          tableContainerRef={tableContainerRef}
        />

        {/* Search and Filter */}
        <Box sx={{ mb: spacing.md }}>
          <SearchAndFilter
            searchQuery={pageState.searchQuery}
            filters={pageState.filters}
            projectOptions={pageState.projectOptions}
            assignedFilterOptions={pageState.assignedFilterOptions}
            queryTypeOptions={pageState.queryTypeOptions}
            onSearchChange={pageState.handleSearchChange}
            onFilterChange={pageState.handleFilterChange}
          />
        </Box>

        {/* Query Table */}
        <Paper
          ref={tableContainerRef}
          sx={{
            borderRadius: borderRadius.lg,
            boxShadow: shadows.lg,
            border: `1px solid ${colors.borderLight}`,
            background: colors.backgroundWhite,
            overflow: 'hidden',
          }}
        >
          <QueryTable
            loading={pageState.loading}
            total={pageState.total}
            paginatedProjects={paginatedProjects}
            groupedQueriesByProject={groupedQueriesByProject}
            expandedProjects={pageState.expandedProjects}
            projectsCatalog={projectsCatalog}
            tableAssignedFilter={pageState.tableAssignedFilter}
            assignedFilterOptions={pageState.assignedFilterOptions}
            onTableAssignedFilterChange={handleTableAssignedFilterChange}
            onToggleProject={pageState.toggleProject}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onReopen={operations.handleReopen}
            onClose={handleClose}
            reopeningId={operations.reopeningId}
            closingId={operations.closingId}
            selectedRegionCard={pageState.selectedRegionCard}
            tableContainerRef={tableContainerRef}
            page={pageState.page}
            rowsPerPage={pageState.rowsPerPage}
            handleChangePage={pageState.handleChangePage}
            handleChangeRowsPerPage={pageState.handleChangeRowsPerPage}
          />
        </Paper>
      </Box>

      {/* Modals */}
      <EscalateQueryDialog
        open={modals.escalateDialogOpen}
        query={modals.escalateTarget}
        submitting={operations.escalatingId === modals.escalateTarget?._id}
        onClose={() => {
          if (operations.escalatingId === modals.escalateTarget?._id) return;
          modals.handleCloseEscalateDialog();
        }}
        onConfirm={handleConfirmEscalation}
      />

      <ViewQueryModal
        open={modals.viewModalOpen}
        queryId={modals.selectedQuery?._id || modals.selectedQuery?.queryId}
        query={modals.selectedQuery}
        onClose={modals.handleCloseViewModal}
        onEdit={(query) => {
          modals.handleCloseViewModal();
          handleEdit(query);
        }}
        onDelete={(query) => {
          modals.handleCloseViewModal();
          handleDelete(query);
        }}
        onQueryUpdated={fetchQueries}
      />

      <EditQueryModal
        open={modals.editModalOpen}
        query={modals.selectedQuery}
        onClose={modals.handleCloseEditModal}
        onQueryUpdated={fetchQueries}
      />

      <ConfirmationDialog
        open={modals.deleteDialogOpen}
        title="Delete Query"
        message="Are you sure you want to delete this query? This action cannot be undone."
        onConfirm={handleConfirmDelete}
        onCancel={modals.handleCloseDeleteDialog}
        variant="danger"
      />

      <ConfirmationDialog
        open={modals.closeDialogOpen}
        title="Close Query"
        message="Are you sure you want to close this query?"
        onConfirm={handleConfirmClose}
        onCancel={modals.handleCloseCloseDialog}
        confirmText="Close"
        cancelText="Cancel"
      />
    </Box>
  );
};

export default RaiseQuery;
